// Chemistry snippets for the question editor's symbol palette.
// Each entry is [latex, description]; the description is what search matches on.
// Formulae use mhchem's \ce{…}; every snippet must render in KaTeX + mhchem.

const ELEMENTS =
  "H Hydrogen,He Helium,Li Lithium,Be Beryllium,B Boron,C Carbon,N Nitrogen,O Oxygen,F Fluorine,Ne Neon," +
  "Na Sodium,Mg Magnesium,Al Aluminium,Si Silicon,P Phosphorus,S Sulfur,Cl Chlorine,Ar Argon,K Potassium,Ca Calcium," +
  "Sc Scandium,Ti Titanium,V Vanadium,Cr Chromium,Mn Manganese,Fe Iron,Co Cobalt,Ni Nickel,Cu Copper,Zn Zinc," +
  "Ga Gallium,Ge Germanium,As Arsenic,Se Selenium,Br Bromine,Kr Krypton,Rb Rubidium,Sr Strontium,Y Yttrium,Zr Zirconium," +
  "Nb Niobium,Mo Molybdenum,Tc Technetium,Ru Ruthenium,Rh Rhodium,Pd Palladium,Ag Silver,Cd Cadmium,In Indium,Sn Tin," +
  "Sb Antimony,Te Tellurium,I Iodine,Xe Xenon,Cs Caesium,Ba Barium,La Lanthanum,Ce Cerium,Pr Praseodymium,Nd Neodymium," +
  "Pm Promethium,Sm Samarium,Eu Europium,Gd Gadolinium,Tb Terbium,Dy Dysprosium,Ho Holmium,Er Erbium,Tm Thulium,Yb Ytterbium," +
  "Lu Lutetium,Hf Hafnium,Ta Tantalum,W Tungsten,Re Rhenium,Os Osmium,Ir Iridium,Pt Platinum,Au Gold,Hg Mercury," +
  "Tl Thallium,Pb Lead,Bi Bismuth,Po Polonium,At Astatine,Rn Radon,Fr Francium,Ra Radium,Ac Actinium,Th Thorium," +
  "Pa Protactinium,U Uranium,Np Neptunium,Pu Plutonium,Am Americium,Cm Curium,Bk Berkelium,Cf Californium,Es Einsteinium,Fm Fermium," +
  "Md Mendelevium,No Nobelium,Lr Lawrencium,Rf Rutherfordium,Db Dubnium,Sg Seaborgium,Bh Bohrium,Hs Hassium,Mt Meitnerium,Ds Darmstadtium," +
  "Rg Roentgenium,Cn Copernicium,Nh Nihonium,Fl Flerovium,Mc Moscovium,Lv Livermorium,Ts Tennessine,Og Oganesson";

const elementSymbols = ELEMENTS.split(",").map((entry, index) => {
  const [symbol, name] = entry.split(" ");
  return [`\\ce{${symbol}}`, `${name} (Z = ${index + 1})`];
});

const chemistrySymbols = [
  {
    title: "Most used",
    symbols: [
      ["\\ce{->}", "Reaction arrow"],
      ["\\ce{<=>}", "Equilibrium arrow"],
      ["\\ce{<->}", "Resonance arrow"],
      ["\\ce{->[\\Delta]}", "Heat over arrow"],
      ["\\ce{A + B -> C + D}", "Reaction template"],
      ["\\ce{A + B <=> C + D}", "Equilibrium reaction template"],
      ["\\ce{H2O}", "Water"],
      ["\\ce{H+}", "Hydrogen ion"],
      ["\\ce{OH-}", "Hydroxide ion"],
      ["\\ce{SO4^2-}", "Sulfate ion"],
      ["\\ce{^}", "Gas evolved (up arrow)"],
      ["\\ce{v}", "Precipitate (down arrow)"],
      ["\\ce{(s)}", "Solid"],
      ["\\ce{(l)}", "Liquid"],
      ["\\ce{(g)}", "Gas"],
      ["\\ce{(aq)}", "Aqueous"],
      ["\\Delta H", "Enthalpy change"],
      ["\\Delta G", "Gibbs energy change"],
      ["\\Delta S", "Entropy change"],
      ["E^{\\circ}", "Standard electrode potential"],
      ["K_{c}", "Equilibrium constant (Kc)"],
      ["K_{p}", "Equilibrium constant (Kp)"],
      ["\\text{pH}", "pH"],
      ["[\\ce{A}]", "Concentration of A"],
      ["\\delta^{+}", "Partial positive"],
      ["\\delta^{-}", "Partial negative"],
      ["sp^{3}", "sp3 hybridisation"],
      ["\\ce{^{A}_{Z}X}", "Isotope notation"],
      ["\\ce{[Fe(CN)6]^4-}", "Complex ion"],
      ["\\ce{CuSO4*5H2O}", "Hydrated salt"],
    ],
  },
  {
    title: "Common molecules",
    symbols: [
      ["\\ce{H2}", "Hydrogen gas"],
      ["\\ce{O2}", "Oxygen gas"],
      ["\\ce{O3}", "Ozone"],
      ["\\ce{N2}", "Nitrogen gas"],
      ["\\ce{Cl2}", "Chlorine gas"],
      ["\\ce{Br2}", "Bromine"],
      ["\\ce{I2}", "Iodine"],
      ["\\ce{F2}", "Fluorine gas"],
      ["\\ce{P4}", "White phosphorus"],
      ["\\ce{S8}", "Rhombic sulfur"],
      ["\\ce{H2O}", "Water"],
      ["\\ce{D2O}", "Heavy water"],
      ["\\ce{H2O2}", "Hydrogen peroxide"],
      ["\\ce{CO}", "Carbon monoxide"],
      ["\\ce{CO2}", "Carbon dioxide"],
      ["\\ce{SO2}", "Sulfur dioxide"],
      ["\\ce{SO3}", "Sulfur trioxide"],
      ["\\ce{NO}", "Nitric oxide"],
      ["\\ce{NO2}", "Nitrogen dioxide"],
      ["\\ce{N2O}", "Nitrous oxide"],
      ["\\ce{N2O4}", "Dinitrogen tetroxide"],
      ["\\ce{N2O5}", "Dinitrogen pentoxide"],
      ["\\ce{NH3}", "Ammonia"],
      ["\\ce{PH3}", "Phosphine"],
      ["\\ce{H2S}", "Hydrogen sulfide"],
      ["\\ce{HF}", "Hydrogen fluoride"],
      ["\\ce{CH4}", "Methane"],
      ["\\ce{C2H6}", "Ethane"],
      ["\\ce{C2H4}", "Ethene"],
      ["\\ce{C2H2}", "Ethyne"],
      ["\\ce{C6H6}", "Benzene"],
      ["\\ce{C6H12O6}", "Glucose"],
      ["\\ce{C12H22O11}", "Sucrose"],
      ["\\ce{NaCl}", "Sodium chloride"],
      ["\\ce{KCl}", "Potassium chloride"],
      ["\\ce{CaO}", "Quicklime"],
      ["\\ce{MgO}", "Magnesium oxide"],
      ["\\ce{Al2O3}", "Alumina"],
      ["\\ce{Fe2O3}", "Iron(III) oxide"],
      ["\\ce{Fe3O4}", "Magnetite"],
      ["\\ce{SiO2}", "Silica"],
      ["\\ce{CuSO4*5H2O}", "Blue vitriol"],
      ["\\ce{FeSO4*7H2O}", "Green vitriol"],
      ["\\ce{CaSO4*1/2H2O}", "Plaster of Paris"],
      ["\\ce{CaSO4*2H2O}", "Gypsum"],
      ["\\ce{Na2CO3*10H2O}", "Washing soda"],
      ["\\ce{NaHCO3}", "Baking soda"],
      ["\\ce{CaOCl2}", "Bleaching powder"],
      ["\\ce{KMnO4}", "Potassium permanganate"],
      ["\\ce{K2Cr2O7}", "Potassium dichromate"],
      ["\\ce{AgNO3}", "Silver nitrate"],
      ["\\ce{BaSO4}", "Barium sulfate"],
      ["\\ce{AgCl}", "Silver chloride"],
      ["\\ce{PCl5}", "Phosphorus pentachloride"],
      ["\\ce{PCl3}", "Phosphorus trichloride"],
      ["\\ce{SF6}", "Sulfur hexafluoride"],
      ["\\ce{XeF2}", "Xenon difluoride"],
      ["\\ce{XeF4}", "Xenon tetrafluoride"],
      ["\\ce{XeF6}", "Xenon hexafluoride"],
      ["\\ce{BF3}", "Boron trifluoride"],
      ["\\ce{B2H6}", "Diborane"],
      ["\\ce{Na2B4O7*10H2O}", "Borax"],
    ],
  },
  {
    title: "Cations",
    symbols: [
      ["\\ce{H+}", "Hydrogen ion"],
      ["\\ce{H3O+}", "Hydronium ion"],
      ["\\ce{Li+}", "Lithium ion"],
      ["\\ce{Na+}", "Sodium ion"],
      ["\\ce{K+}", "Potassium ion"],
      ["\\ce{NH4+}", "Ammonium ion"],
      ["\\ce{Ag+}", "Silver ion"],
      ["\\ce{Cu+}", "Copper(I) ion"],
      ["\\ce{Cu^2+}", "Copper(II) ion"],
      ["\\ce{Mg^2+}", "Magnesium ion"],
      ["\\ce{Ca^2+}", "Calcium ion"],
      ["\\ce{Ba^2+}", "Barium ion"],
      ["\\ce{Sr^2+}", "Strontium ion"],
      ["\\ce{Zn^2+}", "Zinc ion"],
      ["\\ce{Fe^2+}", "Iron(II) ion"],
      ["\\ce{Fe^3+}", "Iron(III) ion"],
      ["\\ce{Al^3+}", "Aluminium ion"],
      ["\\ce{Cr^3+}", "Chromium(III) ion"],
      ["\\ce{Mn^2+}", "Manganese(II) ion"],
      ["\\ce{Co^2+}", "Cobalt(II) ion"],
      ["\\ce{Ni^2+}", "Nickel(II) ion"],
      ["\\ce{Pb^2+}", "Lead(II) ion"],
      ["\\ce{Sn^2+}", "Tin(II) ion"],
      ["\\ce{Sn^4+}", "Tin(IV) ion"],
      ["\\ce{Hg^2+}", "Mercury(II) ion"],
      ["\\ce{Hg2^2+}", "Mercury(I) ion"],
      ["\\ce{M^n+}", "Metal ion (generic)"],
    ],
  },
  {
    title: "Anions",
    symbols: [
      ["\\ce{OH-}", "Hydroxide"],
      ["\\ce{F-}", "Fluoride"],
      ["\\ce{Cl-}", "Chloride"],
      ["\\ce{Br-}", "Bromide"],
      ["\\ce{I-}", "Iodide"],
      ["\\ce{H-}", "Hydride"],
      ["\\ce{O^2-}", "Oxide"],
      ["\\ce{O2^2-}", "Peroxide"],
      ["\\ce{O2-}", "Superoxide"],
      ["\\ce{S^2-}", "Sulfide"],
      ["\\ce{N^3-}", "Nitride"],
      ["\\ce{CN-}", "Cyanide"],
      ["\\ce{SCN-}", "Thiocyanate"],
      ["\\ce{NO2-}", "Nitrite"],
      ["\\ce{NO3-}", "Nitrate"],
      ["\\ce{SO3^2-}", "Sulfite"],
      ["\\ce{SO4^2-}", "Sulfate"],
      ["\\ce{HSO4-}", "Hydrogen sulfate"],
      ["\\ce{S2O3^2-}", "Thiosulfate"],
      ["\\ce{CO3^2-}", "Carbonate"],
      ["\\ce{HCO3-}", "Bicarbonate"],
      ["\\ce{PO4^3-}", "Phosphate"],
      ["\\ce{HPO4^2-}", "Hydrogen phosphate"],
      ["\\ce{H2PO4-}", "Dihydrogen phosphate"],
      ["\\ce{CH3COO-}", "Acetate"],
      ["\\ce{C2O4^2-}", "Oxalate"],
      ["\\ce{MnO4-}", "Permanganate"],
      ["\\ce{MnO4^2-}", "Manganate"],
      ["\\ce{CrO4^2-}", "Chromate"],
      ["\\ce{Cr2O7^2-}", "Dichromate"],
      ["\\ce{ClO-}", "Hypochlorite"],
      ["\\ce{ClO2-}", "Chlorite"],
      ["\\ce{ClO3-}", "Chlorate"],
      ["\\ce{ClO4-}", "Perchlorate"],
      ["\\ce{AlO2-}", "Aluminate"],
      ["\\ce{SiO3^2-}", "Silicate"],
      ["\\ce{BO3^3-}", "Borate"],
      ["\\ce{X-}", "Halide (generic)"],
    ],
  },
  {
    title: "Reaction arrows & conditions",
    symbols: [
      ["\\ce{->}", "Forward reaction"],
      ["\\ce{<-}", "Reverse reaction"],
      ["\\ce{<=>}", "Reversible / equilibrium"],
      ["\\ce{<=>>}", "Equilibrium favouring products"],
      ["\\ce{<<=>}", "Equilibrium favouring reactants"],
      ["\\ce{<->}", "Resonance"],
      ["\\ce{->[\\Delta]}", "Heat"],
      ["\\ce{->[h\\nu]}", "Light (hν)"],
      ["\\ce{->[catalyst]}", "Reagent over arrow"],
      ["\\ce{->[above][below]}", "Text above and below arrow"],
      ["\\ce{->[H+]}", "Acid catalysed"],
      ["\\ce{->[OH-]}", "Base catalysed"],
      ["\\ce{->[Pt][500 K]}", "Catalyst and temperature"],
      ["\\ce{<=>[K_1][K_2]}", "Equilibrium with constants"],
      ["\\ce{^}", "Gas evolved (↑)"],
      ["\\ce{v}", "Precipitate (↓)"],
      ["\\ce{CO2 ^}", "Gas evolved example"],
      ["\\ce{AgCl v}", "Precipitate example"],
      ["\\ce{+}", "Plus"],
      ["\\ce{(s)}", "Solid"],
      ["\\ce{(l)}", "Liquid"],
      ["\\ce{(g)}", "Gas"],
      ["\\ce{(aq)}", "Aqueous"],
      ["\\Delta", "Heat (Δ)"],
      ["h\\nu", "Light / photon energy"],
      ["\\text{conc.}", "Concentrated"],
      ["\\text{dil.}", "Dilute"],
      ["\\text{STP}", "Standard temperature and pressure"],
      ["\\text{NTP}", "Normal temperature and pressure"],
      ["\\text{r.t.}", "Room temperature"],
      ["298\\ \\text{K}", "298 K"],
      ["1\\ \\text{atm}", "1 atmosphere"],
      ["\\ce{2H2 + O2 -> 2H2O}", "Balanced equation example"],
      ["\\ce{N2 + 3H2 <=> 2NH3}", "Haber process"],
    ],
  },
  {
    title: "Bonds & structure",
    symbols: [
      ["\\ce{A-B}", "Single bond"],
      ["\\ce{A=B}", "Double bond"],
      ["\\ce{A#B}", "Triple bond"],
      ["\\ce{A\\bond{~-}B}", "Partial double bond (delocalised)"],
      ["\\ce{A\\bond{...}B}", "Hydrogen / partial bond"],
      ["\\ce{A\\bond{->}B}", "Coordinate (dative) bond"],
      ["\\ce{-}", "Bond (dash)"],
      ["\\ce{=}", "Double bond (symbol)"],
      ["\\ce{#}", "Triple bond (symbol)"],
      ["\\delta^{+}", "Partial positive charge"],
      ["\\delta^{-}", "Partial negative charge"],
      ["\\ce{Cl^.}", "Free radical"],
      ["\\ce{CH3^.}", "Methyl radical"],
      ["\\ce{CH3+}", "Carbocation"],
      ["\\ce{CH3-}", "Carbanion"],
      ["\\ddot{\\text{N}}", "Lone pair (dots)"],
      ["\\cdot", "Electron dot"],
      ["\\ce{[ ]}", "Brackets"],
      ["\\sigma", "Sigma bond"],
      ["\\pi", "Pi bond"],
      ["\\sigma^{*}", "Sigma antibonding"],
      ["\\pi^{*}", "Pi antibonding"],
      ["\\text{B.O.} = \\frac{N_{b} - N_{a}}{2}", "Bond order"],
      ["\\mu = q \\times d", "Dipole moment"],
      ["\\text{F.C.} = V - L - \\frac{1}{2}S", "Formal charge"],
      ["sp", "sp hybridisation"],
      ["sp^{2}", "sp2 hybridisation"],
      ["sp^{3}", "sp3 hybridisation"],
      ["sp^{3}d", "sp3d hybridisation"],
      ["sp^{3}d^{2}", "sp3d2 hybridisation"],
      ["dsp^{2}", "dsp2 hybridisation"],
      ["d^{2}sp^{3}", "d2sp3 hybridisation"],
      ["sp^{3}d^{3}", "sp3d3 hybridisation"],
    ],
  },
  {
    title: "Atomic structure",
    symbols: [
      ["n", "Principal quantum number"],
      ["l", "Azimuthal quantum number"],
      ["m_{l}", "Magnetic quantum number"],
      ["m_{s} = \\pm\\frac{1}{2}", "Spin quantum number"],
      ["1s^{2}", "Orbital occupancy"],
      ["1s^{2}\\,2s^{2}\\,2p^{6}", "Electronic configuration"],
      ["[\\text{Ne}]\\,3s^{1}", "Noble gas core (Ne)"],
      ["[\\text{Ar}]\\,3d^{10}\\,4s^{1}", "Noble gas core (Ar)"],
      ["[\\text{Xe}]\\,4f^{14}\\,5d^{10}\\,6s^{2}", "Noble gas core (Xe)"],
      ["p_{x}", "p-x orbital"],
      ["p_{y}", "p-y orbital"],
      ["p_{z}", "p-z orbital"],
      ["d_{xy}", "d-xy orbital"],
      ["d_{yz}", "d-yz orbital"],
      ["d_{xz}", "d-xz orbital"],
      ["d_{x^{2}-y^{2}}", "d-x²−y² orbital"],
      ["d_{z^{2}}", "d-z² orbital"],
      ["\\uparrow\\downarrow", "Paired electrons"],
      ["\\uparrow", "Unpaired electron"],
      ["\\boxed{\\uparrow\\downarrow}", "Filled orbital box"],
      ["\\boxed{\\uparrow}", "Half-filled orbital box"],
      ["\\psi", "Wave function"],
      ["\\psi^{2}", "Probability density"],
      ["\\hat{H}\\psi = E\\psi", "Schrödinger equation"],
      ["\\text{Nodes} = n - l - 1", "Radial nodes"],
      ["\\sqrt{l(l+1)}\\,\\frac{h}{2\\pi}", "Orbital angular momentum"],
      ["\\mu = \\sqrt{n(n+2)}\\ \\text{BM}", "Spin-only magnetic moment"],
      ["E_{n} = -13.6\\frac{Z^{2}}{n^{2}}\\ \\text{eV}", "Energy of nth orbit"],
      ["r_{n} = 0.529\\frac{n^{2}}{Z}\\ \\text{Å}", "Radius of nth orbit"],
      ["\\bar{\\nu} = \\frac{1}{\\lambda} = R_{H}Z^{2}\\left(\\frac{1}{n_{1}^{2}} - \\frac{1}{n_{2}^{2}}\\right)", "Rydberg equation"],
      ["\\lambda = \\frac{h}{mv}", "de Broglie wavelength"],
      ["\\Delta x \\cdot \\Delta p \\geq \\frac{h}{4\\pi}", "Heisenberg uncertainty"],
      ["E = h\\nu = \\frac{hc}{\\lambda}", "Photon energy"],
      ["Z_{\\text{eff}} = Z - \\sigma", "Effective nuclear charge"],
      ["\\frac{n(n-1)}{2}", "Number of spectral lines"],
    ],
  },
  {
    title: "Isotopes & nuclear",
    symbols: [
      ["\\ce{^{A}_{Z}X}", "Isotope notation"],
      ["\\ce{^{1}_{1}H}", "Protium"],
      ["\\ce{^{2}_{1}H}", "Deuterium"],
      ["\\ce{^{3}_{1}H}", "Tritium"],
      ["\\ce{^{12}_{6}C}", "Carbon-12"],
      ["\\ce{^{14}_{6}C}", "Carbon-14"],
      ["\\ce{^{16}_{8}O}", "Oxygen-16"],
      ["\\ce{^{235}_{92}U}", "Uranium-235"],
      ["\\ce{^{4}_{2}He}", "Alpha particle"],
      ["\\ce{^{1}_{0}n}", "Neutron"],
      ["\\ce{^{1}_{1}p}", "Proton"],
      ["\\ce{^{0}_{-1}e}", "Beta particle (electron)"],
      ["\\ce{^{0}_{+1}e}", "Positron"],
      ["\\ce{^{0}_{0}\\gamma}", "Gamma ray"],
      ["\\ce{^{238}_{92}U -> ^{234}_{90}Th + ^{4}_{2}He}", "Alpha decay example"],
      ["\\ce{^{14}_{6}C -> ^{14}_{7}N + ^{0}_{-1}e}", "Beta decay example"],
      ["t_{1/2} = \\frac{0.693}{\\lambda}", "Half-life"],
      ["N = N_{0}e^{-\\lambda t}", "Radioactive decay"],
    ],
  },
  {
    title: "Mole concept & solutions",
    symbols: [
      ["n = \\frac{m}{M}", "Moles from mass"],
      ["n = \\frac{N}{N_{A}}", "Moles from particles"],
      ["n = \\frac{V}{22.4\\ \\text{L}}", "Moles of gas at STP"],
      ["N_{A} = 6.022 \\times 10^{23}", "Avogadro's number"],
      ["M = \\frac{n_{\\text{solute}}}{V_{\\text{solution}}(\\text{L})}", "Molarity"],
      ["m = \\frac{n_{\\text{solute}}}{W_{\\text{solvent}}(\\text{kg})}", "Molality"],
      ["N = \\frac{\\text{gram equivalents}}{V(\\text{L})}", "Normality"],
      ["x_{A} = \\frac{n_{A}}{n_{A} + n_{B}}", "Mole fraction"],
      ["\\%\\,(w/w)", "Mass percentage"],
      ["\\%\\,(w/v)", "Mass by volume percentage"],
      ["\\text{ppm} = \\frac{\\text{mass of solute}}{\\text{mass of solution}} \\times 10^{6}", "Parts per million"],
      ["M_{1}V_{1} = M_{2}V_{2}", "Dilution"],
      ["N_{1}V_{1} = N_{2}V_{2}", "Normality equation"],
      ["\\text{Eq. wt.} = \\frac{\\text{Molar mass}}{n\\text{-factor}}", "Equivalent weight"],
      ["\\text{Empirical formula} \\times n = \\text{Molecular formula}", "Empirical and molecular formula"],
      ["p_{A} = x_{A}p_{A}^{\\circ}", "Raoult's law"],
      ["P = x_{A}p_{A}^{\\circ} + x_{B}p_{B}^{\\circ}", "Total vapour pressure"],
      ["\\frac{p^{\\circ} - p}{p^{\\circ}} = x_{B}", "Relative lowering of vapour pressure"],
      ["\\Delta T_{b} = iK_{b}m", "Elevation of boiling point"],
      ["\\Delta T_{f} = iK_{f}m", "Depression of freezing point"],
      ["\\pi = iCRT", "Osmotic pressure"],
      ["i", "van 't Hoff factor"],
      ["\\alpha = \\frac{i - 1}{n - 1}", "Degree of dissociation"],
      ["p = K_{H}x", "Henry's law"],
      ["\\rho = \\frac{m}{V}", "Density"],
    ],
  },
  {
    title: "Gaseous state",
    symbols: [
      ["PV = nRT", "Ideal gas equation"],
      ["\\frac{P_{1}V_{1}}{T_{1}} = \\frac{P_{2}V_{2}}{T_{2}}", "Combined gas law"],
      ["P_{1}V_{1} = P_{2}V_{2}", "Boyle's law"],
      ["\\frac{V_{1}}{T_{1}} = \\frac{V_{2}}{T_{2}}", "Charles's law"],
      ["P_{\\text{total}} = P_{1} + P_{2} + P_{3}", "Dalton's law"],
      ["p_{A} = x_{A}P_{\\text{total}}", "Partial pressure"],
      ["\\left(P + \\frac{an^{2}}{V^{2}}\\right)(V - nb) = nRT", "van der Waals equation"],
      ["Z = \\frac{PV}{nRT}", "Compressibility factor"],
      ["\\frac{r_{1}}{r_{2}} = \\sqrt{\\frac{M_{2}}{M_{1}}}", "Graham's law"],
      ["u_{\\text{rms}} = \\sqrt{\\frac{3RT}{M}}", "RMS speed"],
      ["T_{c} = \\frac{8a}{27Rb}", "Critical temperature"],
      ["R = 0.0821\\ \\text{L atm mol}^{-1}\\text{K}^{-1}", "Gas constant (L atm)"],
      ["R = 8.314\\ \\text{J mol}^{-1}\\text{K}^{-1}", "Gas constant (J)"],
      ["R = 2\\ \\text{cal mol}^{-1}\\text{K}^{-1}", "Gas constant (cal)"],
    ],
  },
  {
    title: "Thermodynamics",
    symbols: [
      ["\\Delta H", "Enthalpy change"],
      ["\\Delta S", "Entropy change"],
      ["\\Delta G", "Gibbs energy change"],
      ["\\Delta U", "Internal energy change"],
      ["\\Delta H^{\\circ}", "Standard enthalpy change"],
      ["\\Delta G^{\\circ}", "Standard Gibbs energy"],
      ["\\Delta_{f}H^{\\circ}", "Standard enthalpy of formation"],
      ["\\Delta_{c}H^{\\circ}", "Enthalpy of combustion"],
      ["\\Delta_{r}H^{\\circ}", "Enthalpy of reaction"],
      ["\\Delta_{\\text{vap}}H", "Enthalpy of vaporisation"],
      ["\\Delta_{\\text{fus}}H", "Enthalpy of fusion"],
      ["\\Delta_{\\text{sub}}H", "Enthalpy of sublimation"],
      ["\\Delta_{\\text{hyd}}H", "Enthalpy of hydration"],
      ["\\Delta U = q + w", "First law (chemistry)"],
      ["w = -P_{\\text{ext}}\\Delta V", "Work (irreversible)"],
      ["w = -2.303\\,nRT\\log\\frac{V_{2}}{V_{1}}", "Work (reversible isothermal)"],
      ["\\Delta H = \\Delta U + \\Delta n_{g}RT", "ΔH and ΔU"],
      ["\\Delta G = \\Delta H - T\\Delta S", "Gibbs equation"],
      ["\\Delta G^{\\circ} = -RT\\ln K", "Gibbs energy and K"],
      ["\\Delta G^{\\circ} = -2.303\\,RT\\log K", "Gibbs energy and K (log)"],
      ["\\Delta G^{\\circ} = -nFE^{\\circ}_{\\text{cell}}", "Gibbs energy and EMF"],
      ["\\Delta S = \\frac{q_{\\text{rev}}}{T}", "Entropy (reversible heat)"],
      ["\\Delta_{r}H = \\sum \\Delta_{f}H_{\\text{products}} - \\sum \\Delta_{f}H_{\\text{reactants}}", "Hess's law"],
      ["\\Delta_{r}H = \\sum \\text{B.E.}_{\\text{reactants}} - \\sum \\text{B.E.}_{\\text{products}}", "Bond enthalpy method"],
      ["q = mc\\Delta T", "Heat absorbed"],
      ["C_{p} - C_{v} = R", "Molar heat capacities"],
      ["q_{p} = \\Delta H", "Heat at constant pressure"],
      ["q_{v} = \\Delta U", "Heat at constant volume"],
    ],
  },
  {
    title: "Equilibrium, acids & bases",
    symbols: [
      ["K_{c} = \\frac{[\\ce{C}]^{c}[\\ce{D}]^{d}}{[\\ce{A}]^{a}[\\ce{B}]^{b}}", "Kc expression"],
      ["K_{p} = \\frac{p_{C}^{c}\\,p_{D}^{d}}{p_{A}^{a}\\,p_{B}^{b}}", "Kp expression"],
      ["K_{p} = K_{c}(RT)^{\\Delta n}", "Kp and Kc"],
      ["Q_{c}", "Reaction quotient"],
      ["K_{eq}", "Equilibrium constant"],
      ["K_{a}", "Acid dissociation constant"],
      ["K_{b}", "Base dissociation constant"],
      ["K_{w} = [\\ce{H+}][\\ce{OH-}] = 10^{-14}", "Ionic product of water"],
      ["K_{a} \\times K_{b} = K_{w}", "Ka Kb relation"],
      ["K_{h} = \\frac{K_{w}}{K_{a}}", "Hydrolysis constant"],
      ["K_{sp}", "Solubility product"],
      ["K_{sp} = s^{2}", "Ksp for AB salt"],
      ["K_{sp} = 4s^{3}", "Ksp for AB2 salt"],
      ["\\text{pH} = -\\log[\\ce{H+}]", "pH"],
      ["\\text{pOH} = -\\log[\\ce{OH-}]", "pOH"],
      ["\\text{pH} + \\text{pOH} = 14", "pH + pOH"],
      ["\\text{p}K_{a} = -\\log K_{a}", "pKa"],
      ["\\text{p}K_{b}", "pKb"],
      ["\\text{pH} = \\text{p}K_{a} + \\log\\frac{[\\text{salt}]}{[\\text{acid}]}", "Henderson equation (acidic buffer)"],
      ["\\text{pOH} = \\text{p}K_{b} + \\log\\frac{[\\text{salt}]}{[\\text{base}]}", "Henderson equation (basic buffer)"],
      ["\\alpha = \\sqrt{\\frac{K_{a}}{C}}", "Ostwald's dilution law"],
      ["[\\ce{H+}] = \\sqrt{K_{a}C}", "H+ of weak acid"],
      ["\\text{pH} = 7 + \\frac{1}{2}(\\text{p}K_{a} - \\text{p}K_{b})", "pH of weak acid–weak base salt"],
      ["\\ce{HCl}", "Hydrochloric acid"],
      ["\\ce{H2SO4}", "Sulfuric acid"],
      ["\\ce{HNO3}", "Nitric acid"],
      ["\\ce{H3PO4}", "Phosphoric acid"],
      ["\\ce{CH3COOH}", "Acetic acid"],
      ["\\ce{HCOOH}", "Formic acid"],
      ["\\ce{H2CO3}", "Carbonic acid"],
      ["\\ce{HClO4}", "Perchloric acid"],
      ["\\ce{NaOH}", "Sodium hydroxide"],
      ["\\ce{KOH}", "Potassium hydroxide"],
      ["\\ce{Ca(OH)2}", "Calcium hydroxide"],
      ["\\ce{NH4OH}", "Ammonium hydroxide"],
      ["\\ce{NH4Cl}", "Ammonium chloride"],
      ["\\ce{CaCO3}", "Calcium carbonate"],
      ["\\ce{Na2CO3}", "Sodium carbonate"],
      ["\\ce{CH3COONa}", "Sodium acetate"],
    ],
  },
  {
    title: "Electrochemistry",
    symbols: [
      ["E^{\\circ}", "Standard electrode potential"],
      ["E_{\\text{cell}}", "Cell potential"],
      ["E^{\\circ}_{\\text{cell}} = E^{\\circ}_{\\text{cathode}} - E^{\\circ}_{\\text{anode}}", "Standard cell EMF"],
      ["E^{\\circ}_{\\ce{Zn^2+/Zn}}", "Electrode potential (Zn)"],
      ["E = E^{\\circ} - \\frac{0.0591}{n}\\log Q", "Nernst equation (298 K)"],
      ["E = E^{\\circ} - \\frac{RT}{nF}\\ln Q", "Nernst equation"],
      ["E^{\\circ}_{\\text{cell}} = \\frac{0.0591}{n}\\log K_{c}", "EMF and equilibrium constant"],
      ["\\ce{Zn(s) | Zn^2+(aq) || Cu^2+(aq) | Cu(s)}", "Cell notation (Daniell cell)"],
      ["||", "Salt bridge"],
      ["F = 96500\\ \\text{C mol}^{-1}", "Faraday constant"],
      ["W = ZIt", "Faraday's first law"],
      ["\\frac{W_{1}}{W_{2}} = \\frac{E_{1}}{E_{2}}", "Faraday's second law"],
      ["Z = \\frac{E}{F}", "Electrochemical equivalent"],
      ["G = \\frac{1}{R}", "Conductance"],
      ["\\kappa = G \\times \\frac{l}{A}", "Conductivity"],
      ["G^{*} = \\frac{l}{A}", "Cell constant"],
      ["\\Lambda_{m} = \\frac{\\kappa \\times 1000}{M}", "Molar conductivity"],
      ["\\Lambda_{m}^{\\circ}", "Limiting molar conductivity"],
      ["\\Lambda_{m} = \\Lambda_{m}^{\\circ} - A\\sqrt{c}", "Debye–Hückel–Onsager"],
      ["\\Lambda_{m}^{\\circ} = \\nu_{+}\\lambda_{+}^{\\circ} + \\nu_{-}\\lambda_{-}^{\\circ}", "Kohlrausch's law"],
      ["\\alpha = \\frac{\\Lambda_{m}}{\\Lambda_{m}^{\\circ}}", "Degree of dissociation"],
      ["\\text{S cm}^{2}\\text{mol}^{-1}", "Molar conductivity unit"],
      ["\\text{S m}^{-1}", "Conductivity unit"],
    ],
  },
  {
    title: "Chemical kinetics",
    symbols: [
      ["\\text{Rate} = k[\\ce{A}]^{m}[\\ce{B}]^{n}", "Rate law"],
      ["-\\frac{d[\\ce{A}]}{dt}", "Rate of disappearance"],
      ["\\frac{d[\\ce{P}]}{dt}", "Rate of formation"],
      ["-\\frac{1}{a}\\frac{d[\\ce{A}]}{dt} = \\frac{1}{c}\\frac{d[\\ce{C}]}{dt}", "Rate in terms of stoichiometry"],
      ["[\\ce{A}]_{t} = [\\ce{A}]_{0} - kt", "Zero-order integrated"],
      ["t_{1/2} = \\frac{[\\ce{A}]_{0}}{2k}", "Zero-order half-life"],
      ["k = \\frac{2.303}{t}\\log\\frac{[\\ce{A}]_{0}}{[\\ce{A}]}", "First-order rate constant"],
      ["[\\ce{A}]_{t} = [\\ce{A}]_{0}e^{-kt}", "First-order integrated"],
      ["t_{1/2} = \\frac{0.693}{k}", "First-order half-life"],
      ["k = Ae^{-E_{a}/RT}", "Arrhenius equation"],
      ["\\log\\frac{k_{2}}{k_{1}} = \\frac{E_{a}}{2.303R}\\left(\\frac{1}{T_{1}} - \\frac{1}{T_{2}}\\right)", "Arrhenius (two temperatures)"],
      ["\\ln k = \\ln A - \\frac{E_{a}}{RT}", "Arrhenius (log form)"],
      ["E_{a}", "Activation energy"],
      ["\\frac{k_{T+10}}{k_{T}}", "Temperature coefficient"],
      ["\\text{mol L}^{-1}\\text{s}^{-1}", "Rate unit"],
      ["\\text{s}^{-1}", "First-order k unit"],
    ],
  },
  {
    title: "Organic groups",
    symbols: [
      ["\\ce{R\\bond{-}}", "Alkyl group"],
      ["\\ce{R'}", "Second alkyl group"],
      ["\\ce{Ar\\bond{-}}", "Aryl group"],
      ["\\ce{Ph\\bond{-}}", "Phenyl group"],
      ["\\ce{C6H5\\bond{-}}", "Phenyl (formula)"],
      ["\\ce{CH3\\bond{-}}", "Methyl"],
      ["\\ce{C2H5\\bond{-}}", "Ethyl"],
      ["\\ce{Me}", "Methyl (Me)"],
      ["\\ce{Et}", "Ethyl (Et)"],
      ["\\ce{-OH}", "Hydroxyl"],
      ["\\ce{-OR}", "Alkoxy"],
      ["\\ce{-CHO}", "Aldehyde"],
      ["\\ce{>C=O}", "Ketone / carbonyl"],
      ["\\ce{-COOH}", "Carboxylic acid"],
      ["\\ce{-COO-}", "Carboxylate"],
      ["\\ce{-COOR}", "Ester"],
      ["\\ce{-COCl}", "Acid chloride"],
      ["\\ce{-CONH2}", "Amide"],
      ["\\ce{(RCO)2O}", "Anhydride"],
      ["\\ce{-NH2}", "Amino"],
      ["\\ce{-NO2}", "Nitro"],
      ["\\ce{-C#N}", "Nitrile / cyano"],
      ["\\ce{-N#C}", "Isocyanide"],
      ["\\ce{-N2+}", "Diazonium"],
      ["\\ce{-SO3H}", "Sulfonic acid"],
      ["\\ce{-X}", "Halide"],
      ["\\ce{>C=C<}", "Alkene"],
      ["\\ce{-C#C-}", "Alkyne"],
      ["\\ce{R-O-R}", "Ether"],
      ["\\ce{R-CO-R}", "Ketone"],
      ["\\ce{CH3-CH2-OH}", "Ethanol"],
      ["\\ce{CH3OH}", "Methanol"],
      ["\\ce{HCHO}", "Formaldehyde"],
      ["\\ce{CH3CHO}", "Acetaldehyde"],
      ["\\ce{CH3COCH3}", "Acetone"],
      ["\\ce{C6H5OH}", "Phenol"],
      ["\\ce{C6H5NH2}", "Aniline"],
      ["\\ce{C6H5CHO}", "Benzaldehyde"],
      ["\\ce{C6H5COOH}", "Benzoic acid"],
      ["\\ce{C6H5CH3}", "Toluene"],
      ["\\ce{CH2=CH2}", "Ethene (structure)"],
      ["\\ce{HC#CH}", "Ethyne (structure)"],
      ["\\ce{CHCl3}", "Chloroform"],
      ["\\ce{CCl4}", "Carbon tetrachloride"],
      ["\\ce{Nu-}", "Nucleophile"],
      ["\\ce{E+}", "Electrophile"],
      ["\\alpha", "Alpha carbon"],
      ["\\beta", "Beta carbon"],
      ["S_{N}1", "SN1"],
      ["S_{N}2", "SN2"],
      ["E1", "E1 elimination"],
      ["E2", "E2 elimination"],
      ["+I", "+I effect"],
      ["-I", "−I effect"],
      ["+M", "+M / +R effect"],
      ["-M", "−M / −R effect"],
      ["\\textit{cis}", "cis"],
      ["\\textit{trans}", "trans"],
      ["(R)", "R configuration"],
      ["(S)", "S configuration"],
      ["(E)", "E isomer"],
      ["(Z)", "Z isomer"],
      ["\\text{D}", "D configuration"],
      ["\\text{L}", "L configuration"],
      ["(+)", "Dextrorotatory"],
      ["(-)", "Laevorotatory"],
      ["[\\alpha]_{D}", "Specific rotation"],
      ["2^{n}", "Number of stereoisomers"],
    ],
  },
  {
    title: "Organic reagents",
    symbols: [
      ["\\ce{->[LiAlH4]}", "Lithium aluminium hydride"],
      ["\\ce{->[NaBH4]}", "Sodium borohydride"],
      ["\\ce{->[H2/Pd]}", "Hydrogenation (Pd)"],
      ["\\ce{->[H2/Ni]}", "Hydrogenation (Ni)"],
      ["\\ce{->[H2][Pd/BaSO4]}", "Lindlar / Rosenmund"],
      ["\\ce{->[Na/liq. NH3]}", "Birch reduction"],
      ["\\ce{->[Zn-Hg/HCl]}", "Clemmensen reduction"],
      ["\\ce{->[NH2NH2/KOH]}", "Wolff–Kishner reduction"],
      ["\\ce{->[DIBAL-H]}", "DIBAL-H"],
      ["\\ce{->[Sn/HCl]}", "Tin and HCl"],
      ["\\ce{->[KMnO4/H+]}", "Acidic permanganate"],
      ["\\ce{->[KMnO4/OH-]}", "Alkaline permanganate"],
      ["\\ce{->[K2Cr2O7/H+]}", "Acidic dichromate"],
      ["\\ce{->[PCC]}", "PCC"],
      ["\\ce{->[CrO3]}", "Chromium trioxide"],
      ["\\ce{->[O3][Zn/H2O]}", "Ozonolysis"],
      ["\\ce{->[OsO4]}", "Osmium tetroxide"],
      ["\\ce{->[Tollens' reagent]}", "Tollens' reagent"],
      ["\\ce{->[Fehling's solution]}", "Fehling's solution"],
      ["\\ce{->[Br2/CCl4]}", "Bromine in CCl4"],
      ["\\ce{->[Br2/FeBr3]}", "Bromination (Lewis acid)"],
      ["\\ce{->[Br2 water]}", "Bromine water"],
      ["\\ce{->[Cl2/h\\nu]}", "Free-radical chlorination"],
      ["\\ce{->[NBS]}", "N-bromosuccinimide"],
      ["\\ce{->[HBr][peroxide]}", "Anti-Markovnikov addition"],
      ["\\ce{->[conc. HNO3][conc. H2SO4]}", "Nitration"],
      ["\\ce{->[conc. H2SO4]}", "Sulfonation / dehydration"],
      ["\\ce{->[CH3Cl/AlCl3]}", "Friedel–Crafts alkylation"],
      ["\\ce{->[CH3COCl/AlCl3]}", "Friedel–Crafts acylation"],
      ["\\ce{->[SOCl2]}", "Thionyl chloride"],
      ["\\ce{->[PCl5]}", "Phosphorus pentachloride"],
      ["\\ce{->[PCl3]}", "Phosphorus trichloride"],
      ["\\ce{->[alc. KOH]}", "Alcoholic KOH (elimination)"],
      ["\\ce{->[aq. KOH]}", "Aqueous KOH (substitution)"],
      ["\\ce{->[NaNH2]}", "Sodamide"],
      ["\\ce{->[NaOH/CaO][\\Delta]}", "Decarboxylation (soda lime)"],
      ["\\ce{->[NaNO2/HCl][273-278 K]}", "Diazotisation"],
      ["\\ce{->[CuCl/HCl]}", "Sandmeyer (chloride)"],
      ["\\ce{->[CuCN/KCN]}", "Sandmeyer (cyanide)"],
      ["\\ce{->[H3PO2]}", "Hypophosphorous acid"],
      ["\\ce{->[CHCl3/KOH]}", "Carbylamine / Reimer–Tiemann"],
      ["\\ce{->[Br2/NaOH]}", "Hofmann bromamide"],
      ["\\ce{->[I2/NaOH]}", "Iodoform test"],
      ["\\ce{->[Mg][dry ether]}", "Grignard formation"],
      ["\\ce{RMgX}", "Grignard reagent"],
      ["\\ce{->[H3O+]}", "Acidic hydrolysis / work-up"],
      ["\\ce{->[B2H6][H2O2/OH-]}", "Hydroboration–oxidation"],
      ["\\ce{->[Hg^2+/H2SO4]}", "Hydration of alkyne"],
      ["\\ce{->[dil. H2SO4]}", "Dilute acid"],
      ["\\ce{->[Na][dry ether]}", "Wurtz reaction"],
      ["\\ce{->[conc. NaOH]}", "Cannizzaro reaction"],
      ["\\ce{->[dil. NaOH]}", "Aldol condensation"],
      ["\\ce{->[HCN]}", "Cyanohydrin formation"],
      ["\\ce{->[NH2OH]}", "Oxime formation"],
      ["\\ce{->[Zn dust][\\Delta]}", "Zinc dust distillation"],
      ["\\ce{->[CO2][H3O+]}", "Carboxylation (Kolbe)"],
    ],
  },
  {
    title: "Coordination compounds",
    symbols: [
      ["\\ce{[Fe(CN)6]^4-}", "Hexacyanoferrate(II)"],
      ["\\ce{[Fe(CN)6]^3-}", "Hexacyanoferrate(III)"],
      ["\\ce{K4[Fe(CN)6]}", "Potassium ferrocyanide"],
      ["\\ce{K3[Fe(CN)6]}", "Potassium ferricyanide"],
      ["\\ce{[Co(NH3)6]^3+}", "Hexaamminecobalt(III)"],
      ["\\ce{[Co(NH3)5Cl]Cl2}", "Pentaamminechloridocobalt(III) chloride"],
      ["\\ce{[Cu(NH3)4]^2+}", "Tetraamminecopper(II)"],
      ["\\ce{[Ni(CO)4]}", "Nickel tetracarbonyl"],
      ["\\ce{[Ni(CN)4]^2-}", "Tetracyanonickelate(II)"],
      ["\\ce{[NiCl4]^2-}", "Tetrachloronickelate(II)"],
      ["\\ce{[Pt(NH3)2Cl2]}", "Cisplatin"],
      ["\\ce{[Ag(NH3)2]+}", "Diamminesilver(I)"],
      ["\\ce{[Fe(H2O)6]^2+}", "Hexaaquairon(II)"],
      ["\\ce{[Cr(H2O)6]^3+}", "Hexaaquachromium(III)"],
      ["\\ce{[Fe(CO)5]}", "Iron pentacarbonyl"],
      ["\\ce{[ML6]^{n+}}", "Complex ion (generic)"],
      ["\\ce{[Co(en)3]^3+}", "Tris(ethylenediamine)cobalt(III)"],
      ["\\text{en}", "Ethylenediamine"],
      ["\\text{ox}", "Oxalato"],
      ["\\text{EDTA}", "EDTA"],
      ["\\text{py}", "Pyridine"],
      ["\\text{dmg}", "Dimethylglyoxime"],
      ["\\Delta_{o}", "Octahedral splitting"],
      ["\\Delta_{t}", "Tetrahedral splitting"],
      ["\\Delta_{t} = \\frac{4}{9}\\Delta_{o}", "Tetrahedral vs octahedral"],
      ["t_{2g}", "t2g set"],
      ["e_{g}", "eg set"],
      ["t_{2g}^{6}\\,e_{g}^{0}", "Configuration example"],
      ["\\text{CFSE} = (-0.4x + 0.6y)\\Delta_{o}", "Crystal field stabilisation energy"],
      ["P", "Pairing energy"],
      ["\\eta^{5}", "Hapticity"],
      ["\\mu\\text{-}", "Bridging ligand"],
      ["\\kappa", "Donor atom (kappa)"],
      ["\\text{EAN} = Z - \\text{O.S.} + 2 \\times \\text{C.N.}", "Effective atomic number"],
      ["\\beta_{n}", "Overall stability constant"],
    ],
  },
  {
    title: "Units & constants",
    symbols: [
      ["\\text{mol}", "Mole"],
      ["\\text{g mol}^{-1}", "Molar mass unit"],
      ["\\text{mol L}^{-1}", "Molarity unit"],
      ["\\text{M}", "Molar"],
      ["\\text{mol kg}^{-1}", "Molality unit"],
      ["\\text{J mol}^{-1}", "Joule per mole"],
      ["\\text{kJ mol}^{-1}", "Kilojoule per mole"],
      ["\\text{J K}^{-1}\\text{mol}^{-1}", "Entropy unit"],
      ["\\text{L atm}", "Litre atmosphere"],
      ["\\text{atm}", "Atmosphere"],
      ["\\text{bar}", "Bar"],
      ["\\text{Torr}", "Torr"],
      ["\\text{mm Hg}", "Millimetre of mercury"],
      ["\\text{Pa}", "Pascal"],
      ["\\text{pm}", "Picometre"],
      ["\\text{Å}", "Angstrom"],
      ["\\text{nm}", "Nanometre"],
      ["\\text{BM}", "Bohr magneton"],
      ["\\text{u}", "Atomic mass unit"],
      ["\\text{amu}", "Atomic mass unit (amu)"],
      ["\\text{D}", "Debye"],
      ["\\text{K}", "Kelvin"],
      ["^{\\circ}\\text{C}", "Degree Celsius"],
      ["\\text{kcal}", "Kilocalorie"],
      ["\\text{eV}", "Electron volt"],
      ["\\text{V}", "Volt"],
      ["\\text{C}", "Coulomb"],
      ["N_{A} = 6.022 \\times 10^{23}\\ \\text{mol}^{-1}", "Avogadro constant"],
      ["h = 6.626 \\times 10^{-34}\\ \\text{J s}", "Planck constant"],
      ["c = 3 \\times 10^{8}\\ \\text{m s}^{-1}", "Speed of light"],
      ["1\\ \\text{atm} = 1.013 \\times 10^{5}\\ \\text{Pa}", "Atmosphere in pascal"],
      ["22.4\\ \\text{L}", "Molar volume at STP"],
      ["2.303", "ln to log factor"],
    ],
  },
  {
    title: "Periodic table elements",
    symbols: elementSymbols,
  },
];

export default chemistrySymbols;
